import { useMemo } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { ProfileFilter } from '../components/ProfileFilter';
import { DashPanel } from '../components/DashPanel';
import { MetricStat } from '../components/MetricStat';
import { RankedList } from '../components/RankedList';
import { exportCardCsv } from '../utils/csvExport';
import { computeRealAIAnalytics } from '../utils/realAIAnalytics';
import type { CharacterRanking } from '../types/ai';

function characterRows(rows: CharacterRanking[]) {
  return rows.map((r) => ({ label: r.character, value: r.count.toLocaleString('pt-BR') }));
}

function characterCsvRows(rows: CharacterRanking[]): (string | number)[][] {
  return rows.map((r) => [r.character, r.count]);
}

export function AIModule() {
  const { filteredProfiles, profilesLoading } = useDashboard();

  const analytics = useMemo(() => computeRealAIAnalytics(filteredProfiles), [filteredProfiles]);

  return (
    <div>
      <ProfileFilter />

      {!profilesLoading && filteredProfiles.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          Nenhum perfil encontrado para esse filtro no Firestore.
        </p>
      )}

      <div className="mb-6 pb-4 border-b border-border flex flex-wrap gap-8">
        <div>
          <p className="text-xs text-muted-foreground">Desenhos reconhecidos</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.totalDrawings.toLocaleString('pt-BR')}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Índice de confiança do modelo</p>
          <p className="text-xl font-semibold text-foreground tabular-nums">
            {analytics.confidenceIndex}%
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5 max-w-xs">
            {analytics.outcomeBreakdown.direct.toLocaleString('pt-BR')} / {analytics.outcomeBreakdown.total.toLocaleString('pt-BR')} classificações foram emitidas diretamente pelo modelo — não indica que estavam corretas.
          </p>
        </div>
      </div>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Visão geral</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
          <DashPanel padding="sm">
            <MetricStat
              label="Letras"
              value={analytics.typeDistribution.letters}
              suffix={`(${analytics.typeDistribution.lettersPct}%)`}
            />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat
              label="Números"
              value={analytics.typeDistribution.numbers}
              suffix={`(${analytics.typeDistribution.numbersPct}%)`}
            />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat label="Você desenhou" value={analytics.outcomeBreakdown.direct} />
          </DashPanel>
          <DashPanel padding="sm">
            <MetricStat label="Isso parece" value={analytics.outcomeBreakdown.suggestive} />
          </DashPanel>
        </div>
        <p className="text-xs text-muted-foreground">
          {analytics.outcomeBreakdown.directPct}% dos desenhos foram classificados diretamente
          pelo modelo. Isso não significa que estavam corretos — é uma medida de confiança
          operacional, não de acerto.
        </p>
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Caracteres reconhecidos
        </h3>
        <RankedList
          title="Ranking por quantidade reconhecida"
          rows={characterRows(analytics.recognizedCharacters)}
          onExport={() =>
            exportCardCsv(
              'ia-caracteres-reconhecidos',
              ['Caractere', 'Quantidade'],
              characterCsvRows(analytics.recognizedCharacters)
            )
          }
        />
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">
          Confiança do modelo por caractere
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankedList
            title='Mais classificações sugestivas — "Isso parece"'
            rows={characterRows(analytics.mostSuggestiveCharacters)}
            onExport={() =>
              exportCardCsv(
                'ia-caracteres-duvida',
                ['Caractere', '"Isso parece"'],
                characterCsvRows(analytics.mostSuggestiveCharacters)
              )
            }
          />
          <RankedList
            title='Mais classificações diretas — "Você desenhou"'
            rows={characterRows(analytics.mostDirectCharacters)}
            onExport={() =>
              exportCardCsv(
                'ia-caracteres-classificacao-direta',
                ['Caractere', '"Você desenhou"'],
                characterCsvRows(analytics.mostDirectCharacters)
              )
            }
          />
        </div>
      </section>

      <section className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Letras e números mais praticados</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RankedList
            title="Ranking de uso de letras"
            rows={characterRows(analytics.topLetters)}
            onExport={() =>
              exportCardCsv(
                'ia-letras-mais-praticadas',
                ['Letra', 'Uso'],
                characterCsvRows(analytics.topLetters)
              )
            }
          />
          <RankedList
            title="Ranking de uso de números"
            rows={characterRows(analytics.topNumbers)}
            onExport={() =>
              exportCardCsv(
                'ia-numeros-mais-praticados',
                ['Número', 'Uso'],
                characterCsvRows(analytics.topNumbers)
              )
            }
          />
        </div>
      </section>

      <section className="mb-2">
        <h3 className="text-sm font-semibold text-foreground mb-3">Desenhos com melhor acurácia do modelo</h3>
        <RankedList
          title="Maior confiança média do modelo"
          emptyLabel="Sem registros com % de confiança nos perfis selecionados"
          rows={analytics.bestAccuracy.map((r) => ({
            label: r.character,
            value: `${r.avgConfidencePct}% · ${r.totalClassifications.toLocaleString('pt-BR')} classificações`,
            pct: r.avgConfidencePct,
          }))}
          onExport={() =>
            exportCardCsv(
              'ia-melhor-acuracia',
              ['Caractere', 'Acurácia média (%)', 'Classificações'],
              analytics.bestAccuracy.map((r) => [r.character, r.avgConfidencePct, r.totalClassifications])
            )
          }
        />
      </section>
    </div>
  );
}
